import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GroupAccountsResponse } from '../types/response/GroupAccounts';
import { AppDispatch, RootState } from '../../../redux/store';
import { ClientAccounts } from '../../Savings/types/response/ListClientSavings';
import { GetGroupResponse } from '../types/response/GetGroup';
import { GroupRepository } from '../repository/GroupRepo';

// ───────────────────────────────
// Async thunks
// ───────────────────────────────

export const fetchGroup = createAsyncThunk<
	GetGroupResponse,
	number | string,
	{ rejectValue: string }
>('group/fetchGroup', async (groupId, { rejectWithValue }) => {
	try {
		return await GroupRepository.getGroup(groupId);
	} catch (err) {
		return rejectWithValue(typeof err === 'string' ? err : 'Failed to get group');
	}
});

export const fetchGroupAccounts = createAsyncThunk<
	GroupAccountsResponse,
	number | string,
	{ rejectValue: string; dispatch: AppDispatch }
>('group/fetchGroupAccounts', async (groupId, { rejectWithValue }) => {
	try {
		const accounts = await GroupRepository.getGroupAccounts(groupId);
		return accounts;
	} catch (err) {
		return rejectWithValue(typeof err === 'string' ? err : 'Failed to get group accounts');
	}
});

// ───────────────────────────────
// State shape
// ───────────────────────────────

export interface GroupState {
	data?: GetGroupResponse;
	accounts?: GroupAccountsResponse;

	loading: {
		group: boolean;
		accounts: boolean;
	};

	error: {
		group?: string;
		accounts?: string;
	};
}

const initialState: GroupState = {
	loading: {
		group: false,
		accounts: false,
	},
	error: {},
};

// ───────────────────────────────
// Slice
// ───────────────────────────────

const groupSlice = createSlice({
	name: 'group',
	initialState,
	reducers: {
		resetGroupState: () => initialState,
	},
	extraReducers: builder => {
		builder
			// Group
			.addCase(fetchGroup.pending, state => {
				state.loading.group = true;
				state.error.group = undefined;
			})
			.addCase(fetchGroup.fulfilled, (state, action: PayloadAction<GetGroupResponse>) => {
				state.loading.group = false;
				state.data = action.payload;
			})
			.addCase(fetchGroup.rejected, (state, action) => {
				state.loading.group = false;
				state.error.group = action.payload;
			})

			// Group Accounts
			.addCase(fetchGroupAccounts.pending, state => {
				state.loading.accounts = true;
				state.error.accounts = undefined;
			})
			.addCase(fetchGroupAccounts.fulfilled, (state, action: PayloadAction<ClientAccounts>) => {
				state.loading.accounts = false;
				state.accounts = action.payload;
			})
			.addCase(fetchGroupAccounts.rejected, (state, action) => {
				state.loading.accounts = false;
				state.error.accounts = action.payload;
			});
	},
});

// ───────────────────────────────
// Exports
// ───────────────────────────────

export const { resetGroupState } = groupSlice.actions;
export const selectGroupState = (state: RootState) => state.viewGroup;
export default groupSlice.reducer;
